import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusOutlined,
  DeleteOutlined,
  FileWordOutlined,
  DownloadOutlined,
  UploadOutlined,
  EditOutlined,
  TagsOutlined,
  PlusCircleOutlined,
  FileProtectOutlined,
  SaveOutlined,
  FileTextOutlined,
  TeamOutlined,
  FolderOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Modal,
  Form,
  Input,
  Select,
  Button,
  Table,
  Space,
  Checkbox,
  Divider,
  Tag,
  Alert,
  Upload,
  message,
} from 'antd';
import TemplateWordEditorModal from '@/components/document-builder/TemplateWordEditorModal';
import { docGuideSection } from '@/components/document-builder/docGuide';
import { HowToCard } from '@/components/how-to-card';
import DocumentTemplateEditor from '@/components/tiptap-editor/DocumentTemplateEditor';
import TokenChips from '@/components/tiptap-editor/TokenChips';
import { ROLES } from '@/constants';
import { retrieveAllCompanies } from '@/services/companies.service';
import {
  getDocumentTemplateTokens,
  createDocumentTemplate,
  updateDocumentTemplate,
  uploadDocumentTemplate,
  downloadDocumentTemplateFile,
  replaceDocumentTemplateFile,
} from '@/services/documentTemplates.service';
import { useAuthStore } from '@/store/useAuthStore';

const CATEGORY_OPTIONS = ['odluka', 'ugovor', 'potvrda', 'ostalo'];
// Who the document is about — drives where it can be generated later.
const AUDIENCE_OPTIONS = ['employee', 'internal', 'external'];
// Whether creating a document from the template also records an employment
// contract or annex in the employee's contract history ('' = neither).
const CONTRACT_KIND_OPTIONS = ['', 'contract', 'annex'];
// Who receives the created document by email ('' = nobody).
const EMAIL_DELIVERY_OPTIONS = ['', 'employee', 'employeeAndHr'];
// `select` is left out: the builder has no way to enter its options, so it
// rendered an empty dropdown at generation. Fields saved with it still show.
const FIELD_TYPES = ['text', 'number', 'date', 'textarea'];

// Request types a template can auto-bind to (matches backend request.typeOfLeave).
// Labels default to Serbian (primary language) via i18n fallback.
const LEAVE_TYPE_OPTIONS = [
  { value: 'holidayLeave', label: 'Godišnji odmor' },
  { value: 'paidLeaveWedding', label: 'Plaćeno odsustvo — venčanje' },
  { value: 'paidLeaveSlava', label: 'Plaćeno odsustvo — slava' },
  { value: 'paidLeaveDeath', label: 'Plaćeno odsustvo — smrtni slučaj' },
  { value: 'paidLeaveOther', label: 'Plaćeno odsustvo — ostalo' },
  { value: 'nonPaidLeave', label: 'Neplaćeno odsustvo' },
  { value: 'maternityLeave', label: 'Porodiljsko odsustvo' },
  { value: 'pregnancyLeave', label: 'Trudničko bolovanje' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'suspension', label: 'Suspenzija' },
  { value: 'nonMonetaryBenefit', label: 'Nenovčano davanje' },
  { value: 'officialTravel', label: 'Službeni put' },
  { value: 'workFromHome', label: 'Rad od kuće' },
  // Not a leave type: the document is made when a termination is entered.
  { value: 'termination', label: 'Raskid ugovora o radu' },
];

// Everything the author can change, in a comparable form: closing warns only
// when this differs from how the template was opened (turning a checkbox on
// and off again is not a change).
const snapshotTemplateState = ({
  fields,
  contentHtml,
  customFields,
  isGlobal,
  replacementFile,
}) =>
  JSON.stringify({
    name: fields.name || '',
    audience: fields.audience || '',
    category: fields.category || '',
    autoGenerateOnLeaveType: fields.autoGenerateOnLeaveType || '',
    replacesDefaultDocument: Boolean(fields.replacesDefaultDocument),
    contractKind: fields.contractKind || '',
    emailDelivery: fields.emailDelivery || '',
    allowedCompanies: (fields.allowedCompanies || []).map(String).sort(),
    contentHtml: contentHtml || '',
    customFields: (customFields || []).map((f) => ({
      key: f.key || '',
      label: f.label || '',
      type: f.type || 'text',
      options: f.options || [],
      required: Boolean(f.required),
    })),
    isGlobal: Boolean(isGlobal),
    replacementFile: Boolean(replacementFile),
  });

const emptyField = () => ({
  key: '',
  label: '',
  type: 'text',
  options: [],
  required: false,
});

/**
 * Full-screen builder for authoring a document template. Two modes:
 *  - 'builder' (default): rich-text body with a token palette (HTML → Puppeteer).
 *  - 'upload': the client brings a DOCX (`file`); the editor is replaced by a
 *    token reference (which `{tokens}` to type into their Word document). Both
 *    modes share the same config (name, audience, category, auto-generate,
 *    replace-default, global) and ad-hoc field definitions.
 *
 * `defaultAudience` seeds the audience for a NEW template (e.g. 'internal' when
 * opened from the company business-documents screen).
 */
export default function TemplateBuilderModal({
  open,
  onClose,
  companyId,
  template = null,
  onSaved,
  mode = 'builder',
  file = null,
  defaultAudience = 'employee',
}) {
  const { t } = useTranslation(['forms', 'messages']);
  // The context-bound modal follows the app theme; the static Modal.confirm
  // renders outside it with antd's default colours.
  const { modal } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  // Auto-generation on approval only applies to employee-facing documents.
  const audienceValue = Form.useWatch('audience', form) || 'employee';
  // "Replace default document" is meaningful only once a leave type is bound.
  const autoGenLeaveType = Form.useWatch('autoGenerateOnLeaveType', form);
  // A template that records a contract/annex is filled per employee with that
  // contract's details, so it never auto-generates on a leave approval.
  const contractKindValue = Form.useWatch('contractKind', form) || '';
  const [contentHtml, setContentHtml] = useState('');
  const [customFields, setCustomFields] = useState([]);
  const [isGlobal, setIsGlobal] = useState(false);
  // When editing an upload template, a picked replacement DOCX (edited in Word).
  const [replacementFile, setReplacementFile] = useState(null);
  // Set by user edits only (programmatic seeding does not fire the handlers),
  // so closing can warn before a half-written template is thrown away.
  const [isDirty, setIsDirty] = useState(false);
  // The state the template was opened with, to tell a real change from an
  // edit that was undone.
  const initialStateRef = useRef('');
  // The Word template open in the in-app editor with the placeholder palette.
  const [wordEditorOpen, setWordEditorOpen] = useState(false);
  // A Word template just uploaded with "Save and edit in the app": it opens in
  // the editor right away, and this modal closes when the editor does.
  const [uploadedTemplate, setUploadedTemplate] = useState(null);
  const editAfterSaveRef = useRef(false);

  const isEdit = Boolean(template?._id);
  // Uploading a brand-new DOCX (multipart create).
  const isUploadMode = mode === 'upload';
  // Editing the config of an already-uploaded DOCX template (the DOCX source is
  // immutable; only name/audience/binding/ad-hoc fields change).
  const isEditingUpload = isEdit && template?.source === 'upload';
  // Both cases show the DOCX token-reference UI instead of the HTML editor.
  const showUploadUi = isUploadMode || isEditingUpload;

  // Global (cross-company) templates push to many companies at once, so only
  // the two highest HLB roles may author/manage them — not plain `admin`.
  const userRoles = useAuthStore((state) => state.user?.roles) || [];
  const canManageGlobal =
    userRoles.includes(ROLES.techAdmin) || userRoles.includes(ROLES.superAdmin);

  const { data: tokens = [] } = useQuery({
    queryKey: ['documentTemplateTokens'],
    queryFn: getDocumentTemplateTokens,
    enabled: open,
    staleTime: Infinity,
  });

  // Only offer tokens that can actually resolve for this template's audience.
  // Company documents (internal/external) have no employee and no leave request,
  // so employee-* and request-* tokens would always render blank — hide them.
  const visibleTokens = useMemo(() => {
    const isCompanyDoc =
      audienceValue === 'internal' || audienceValue === 'external';
    if (!isCompanyDoc) return tokens;
    return tokens.filter(
      (tk) => tk.group === 'company' || tk.group === 'document'
    );
  }, [tokens, audienceValue]);

  // Company list for the "allowed companies" picker (global templates only).
  const { data: companiesResponse } = useQuery({
    queryKey: ['companiesForTemplateScope'],
    queryFn: () => retrieveAllCompanies({ limit: 1000, offset: 0 }),
    enabled: open && canManageGlobal && isGlobal,
    staleTime: 5 * 60 * 1000,
  });
  const companyOptions = (companiesResponse?.items || []).map((c) => ({
    value: c._id,
    label: c.name,
  }));

  useEffect(() => {
    if (!open) return;
    const global = template?.scope === 'global';
    setIsGlobal(global);
    // Upload mode seeds the name from the picked file; both modes fall back to
    // `defaultAudience` when authoring a brand-new template.
    const fallbackName =
      isUploadMode && file ? file.name.replace(/\.docx$/i, '') : '';
    const fields = {
      name: template?.name || fallbackName,
      audience: template?.audience || defaultAudience || 'employee',
      category: template?.category || 'ostalo',
      autoGenerateOnLeaveType: template?.autoGenerateOnLeaveType || undefined,
      replacesDefaultDocument: Boolean(template?.replacesDefaultDocument),
      contractKind: template?.contractKind || '',
      emailDelivery: template?.emailDelivery || '',
      allowedCompanies: template?.allowedCompanies || [],
    };
    form.setFieldsValue(fields);
    setContentHtml(template?.contentHtml || '');
    setCustomFields(template?.customFields || []);
    setReplacementFile(null);
    setIsDirty(false);
    initialStateRef.current = snapshotTemplateState({
      fields,
      contentHtml: template?.contentHtml || '',
      customFields: template?.customFields || [],
      isGlobal: global,
      replacementFile: null,
    });
  }, [open, template, form, isUploadMode, file, defaultAudience]);

  const customFieldKeys = useMemo(
    () => customFields.map((f) => f.key).filter(Boolean),
    [customFields]
  );

  // Ad-hoc fields defined but never placed in the body: the value typed at
  // generation would have nowhere to go. Word files are checked on the server.
  const unplacedFieldKeys = useMemo(
    () =>
      showUploadUi
        ? []
        : customFieldKeys.filter(
            (key) =>
              !contentHtml.includes(`data-token="${key}"`) &&
              !contentHtml.includes(`{{${key}}}`)
          ),
    [showUploadUi, customFieldKeys, contentHtml]
  );

  // "How to use": what each setting means, then how the text (or the Word file)
  // gets its placeholders. Steps about fields the user does not see here are
  // left out (contract/auto-generate for company documents, global for clients).
  const guideSections = useMemo(
    () => [
      docGuideSection(
        t,
        'settings',
        [
          FileTextOutlined,
          TeamOutlined,
          FolderOutlined,
          FileProtectOutlined,
          MailOutlined,
          ThunderboltOutlined,
          GlobalOutlined,
        ],
        [],
        { employee: audienceValue === 'employee', global: canManageGlobal }
      ),
      showUploadUi
        ? docGuideSection(t, 'word', [
            UploadOutlined,
            EditOutlined,
            FileWordOutlined,
            PlusCircleOutlined,
          ])
        : docGuideSection(t, 'template', [
            EditOutlined,
            TagsOutlined,
            PlusCircleOutlined,
            SaveOutlined,
          ]),
    ],
    [t, showUploadUi, audienceValue, canManageGlobal]
  );

  // Placeholder palette for the in-app Word editor: the tokens this template
  // may use, grouped and translated, plus its own fields (also unsaved ones).
  const wordPalette = useMemo(() => {
    const byGroup = new Map();
    for (const { token, group } of visibleTokens) {
      if (!byGroup.has(group)) byGroup.set(group, []);
      byGroup.get(group).push({
        key: token,
        label: t(`forms:customTemplateTokens.${token}`, token),
      });
    }
    const groups = [...byGroup.entries()].map(([group, groupTokens]) => ({
      title: t(`forms:tokenGroup_${group}`, group),
      tokens: groupTokens,
    }));
    const ownFields = customFields
      .filter((field) => field.key)
      .map((field) => ({ key: field.key, label: field.label || field.key }));
    if (ownFields.length) {
      groups.unshift({
        title: t('forms:tokenGroup_custom', 'Dodatna polja'),
        custom: true,
        tokens: ownFields,
      });
    }
    return {
      hint: t(
        'forms:paletteHint',
        'Postavite kursor u dokument i kliknite oznaku — upisaće se na to mesto.'
      ),
      groups,
    };
  }, [visibleTokens, customFields, t]);

  // Fields with no placeholder in the Word file, reported after a save.
  const warnMissingWordFields = (saved) => {
    if (!saved?.missingFieldKeys?.length) return;
    modal.warning({
      title: t('forms:wordFieldsMissingTitle', 'Neka polja nisu u Word fajlu'),
      content: t('forms:wordFieldsMissingText', {
        defaultValue:
          'U Word fajlu nema oznake za: {{fields}}. Vrednost koja se za njih unese neće se pojaviti u dokumentu. Otvorite fajl u Word-u, upišite oznaku (npr. {{example}}) na mesto gde treba, pa zamenite fajl.',
        fields: saved.missingFieldKeys.map((key) => `{${key}}`).join(', '),
        example: `{${saved.missingFieldKeys[0]}}`,
        interpolation: { escapeValue: false },
      }),
    });
  };

  const changeCustomFields = (updater) => {
    setCustomFields(updater);
    setIsDirty(true);
  };

  const updateField = (index, patch) =>
    changeCustomFields((prev) =>
      prev.map((f, i) => (i === index ? { ...f, ...patch } : f))
    );

  const handleClose = () => {
    const changed =
      isDirty &&
      snapshotTemplateState({
        fields: form.getFieldsValue(true),
        contentHtml,
        customFields,
        isGlobal,
        replacementFile,
      }) !== initialStateRef.current;
    if (!changed) {
      onClose?.();
      return;
    }
    modal.confirm({
      title: t('messages:unsavedChanges'),
      content: t('messages:unsavedChangesWarning'),
      okText: t('forms:yes'),
      cancelText: t('forms:no'),
      onOk: () => onClose?.(),
    });
  };

  const mutation = useMutation({
    mutationFn: async (payload) => {
      // A new DOCX is a multipart create. Everything else (builder create/edit,
      // and editing an existing upload template's config) goes through the JSON
      // create/update endpoints — the DOCX source itself is never re-sent.
      if (isUploadMode) {
        const { file: docx, ...config } = payload;
        return uploadDocumentTemplate(companyId, config, docx);
      }
      // Editing an uploaded template: if the client picked a re-edited DOCX,
      // swap the S3 source first, then persist the config changes.
      if (isEditingUpload && replacementFile) {
        await replaceDocumentTemplateFile(template._id, replacementFile);
      }
      return isEdit
        ? updateDocumentTemplate(template._id, payload)
        : createDocumentTemplate(payload);
    },
    onSuccess: (saved) => {
      message.success(t('forms:templateSaved', 'Šablon sačuvan.'));
      warnMissingWordFields(saved);
      queryClient.invalidateQueries({
        queryKey: ['documentTemplates', companyId],
      });
      onSaved?.();
      if (editAfterSaveRef.current && saved?._id) {
        editAfterSaveRef.current = false;
        setIsDirty(false);
        setUploadedTemplate(saved);
        setWordEditorOpen(true);
        return;
      }
      onClose?.();
    },
    onError: (error) => {
      // Surface the specific conflict (another template already replaces the
      // default document for this leave type); otherwise a generic failure.
      const serverMessage = error?.response?.data?.message;
      message.error(
        serverMessage ||
          t('forms:templateSaveFailed', 'Čuvanje šablona nije uspelo.')
      );
    },
  });

  // `openEditor`: a newly uploaded Word template opens in the in-app editor
  // once it is saved.
  const handleSave = async (openEditor = false) => {
    editAfterSaveRef.current = Boolean(openEditor);
    const values = await form.validateFields();
    if (isUploadMode && !file) {
      message.error(t('forms:noFileSelected', 'Izaberi Word (.docx) fajl.'));
      return;
    }
    const cleanedFields = customFields
      .filter((f) => f.key)
      .map((f) => ({
        key: f.key,
        label: f.label || f.key,
        type: f.type || 'text',
        options: f.type === 'select' ? f.options || [] : [],
        required: Boolean(f.required),
      }));
    const scope = canManageGlobal && isGlobal ? 'global' : 'company';
    const contractKind =
      values.audience === 'employee' ? values.contractKind || '' : '';
    const emailDelivery =
      values.audience === 'employee' ? values.emailDelivery || '' : '';
    const autoGenerateOnLeaveType =
      values.audience === 'employee' && !contractKind
        ? values.autoGenerateOnLeaveType || ''
        : '';
    const replacesDefaultDocument = autoGenerateOnLeaveType
      ? Boolean(values.replacesDefaultDocument)
      : false;
    const shared = {
      // Global templates are not owned by one company.
      company: scope === 'global' ? null : companyId,
      scope,
      allowedCompanies: scope === 'global' ? values.allowedCompanies || [] : [],
      name: values.name,
      audience: values.audience || 'employee',
      category: values.category,
      // Auto-generation on request approval is inherently about an employee —
      // only meaningful for the 'employee' audience. Backend normalizes too.
      autoGenerateOnLeaveType,
      replacesDefaultDocument,
      contractKind,
      emailDelivery,
      customFields: cleanedFields,
    };
    let payload;
    if (isUploadMode) {
      payload = { ...shared, file };
    } else if (isEditingUpload) {
      // Config-only update of an uploaded DOCX — no editor body, no re-upload.
      payload = { ...shared };
    } else {
      payload = { ...shared, source: 'builder', contentHtml };
    }
    if (unplacedFieldKeys.length) {
      modal.confirm({
        title: t('forms:fieldsNotInTextTitle', 'Neka polja nisu u tekstu'),
        content: t('forms:fieldsNotInTextConfirm', {
          defaultValue:
            'Polja {{fields}} nisu ubačena u tekst, pa se vrednost koja se za njih unese neće pojaviti u dokumentu. Sačuvati svejedno?',
          fields: unplacedFieldKeys.join(', '),
          interpolation: { escapeValue: false },
        }),
        okText: t('forms:saveAnyway', 'Sačuvaj svejedno'),
        cancelText: t('forms:backToEditing', 'Vrati se na izmenu'),
        onOk: () => mutation.mutate(payload),
      });
      return;
    }
    mutation.mutate(payload);
  };

  const fieldColumns = [
    {
      title: t('forms:fieldKey', 'Ključ'),
      dataIndex: 'key',
      render: (_, __, index) => (
        <Input
          value={customFields[index].key}
          placeholder={t('forms:fieldKeyPlaceholder')}
          onChange={(e) => updateField(index, { key: e.target.value.trim() })}
        />
      ),
    },
    {
      title: t('forms:fieldLabel', 'Naziv polja'),
      dataIndex: 'label',
      render: (_, __, index) => (
        <Input
          value={customFields[index].label}
          onChange={(e) => updateField(index, { label: e.target.value })}
        />
      ),
    },
    {
      title: t('forms:fieldType', 'Tip'),
      dataIndex: 'type',
      width: 130,
      render: (_, __, index) => (
        <Select
          value={customFields[index].type}
          style={{ width: '100%' }}
          options={(FIELD_TYPES.includes(customFields[index].type)
            ? FIELD_TYPES
            : [...FIELD_TYPES, customFields[index].type]
          ).map((v) => ({ value: v, label: t(`forms:fieldType_${v}`, v) }))}
          onChange={(v) => updateField(index, { type: v })}
        />
      ),
    },
    {
      title: t('forms:fieldRequired', 'Obavezno'),
      dataIndex: 'required',
      width: 90,
      render: (_, __, index) => (
        <Checkbox
          checked={customFields[index].required}
          onChange={(e) => updateField(index, { required: e.target.checked })}
        />
      ),
    },
    {
      title: '',
      width: 48,
      render: (_, __, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() =>
            changeCustomFields((prev) => prev.filter((_, i) => i !== index))
          }
        />
      ),
    },
  ];

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        maskClosable={false}
        keyboard={false}
        title={
          isUploadMode
            ? t('forms:uploadWordTemplate', 'Otpremi Word šablon')
            : isEdit
              ? t('forms:editTemplate', 'Izmena šablona')
              : t('forms:newTemplate', 'Novi šablon')
        }
        width="90vw"
        style={{ top: 20 }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              className="btn-default btn-default--hover"
              onClick={handleClose}
            >
              {t('forms:cancel')}
            </Button>
            {isUploadMode && (
              <Button
                className="btn-default btn-default--hover"
                icon={<EditOutlined />}
                loading={mutation.isPending && editAfterSaveRef.current}
                disabled={mutation.isPending}
                onClick={() => handleSave(true)}
              >
                {t('forms:saveAndEditInApp', 'Sačuvaj i uredi u aplikaciji')}
              </Button>
            )}
            <Button
              type="primary"
              className="btn-primary"
              loading={mutation.isPending && !editAfterSaveRef.current}
              disabled={mutation.isPending}
              onClick={() => handleSave(false)}
            >
              {t('forms:save')}
            </Button>
          </div>
        }
      >
        <HowToCard
          title={t('forms:docGuide.title')}
          sections={guideSections}
          open={!isEdit}
        />
        <Form
          form={form}
          layout="vertical"
          onValuesChange={() => setIsDirty(true)}
        >
          <Space size="large" style={{ width: '100%' }} align="start">
            <Form.Item
              name="name"
              label={t('forms:templateName', 'Naziv šablona')}
              rules={[{ required: true }]}
              style={{ flex: 1, minWidth: 280 }}
            >
              <Input placeholder={t('forms:templateNamePlaceholder')} />
            </Form.Item>
            <Form.Item
              name="audience"
              label={t('forms:templateAudience', 'Namena')}
              tooltip={t(
                'forms:templateAudienceHint',
                'Da li se dokument tiče zaposlenog ili je interna/eksterna poslovna dokumentacija. Samo dokumenti za zaposlene nude se u kartonu zaposlenog.'
              )}
              style={{ minWidth: 200 }}
            >
              <Select
                options={AUDIENCE_OPTIONS.map((v) => ({
                  value: v,
                  label: t(`forms:templateAudience_${v}`, v),
                }))}
              />
            </Form.Item>
            <Form.Item
              name="category"
              label={t('forms:templateCategory', 'Kategorija')}
              style={{ minWidth: 180 }}
            >
              <Select
                options={CATEGORY_OPTIONS.map((v) => ({
                  value: v,
                  label: t(`forms:templateCategory_${v}`, v),
                }))}
              />
            </Form.Item>
            {audienceValue === 'employee' && (
              <Form.Item
                name="contractKind"
                label={t('forms:templateContractKind', 'Evidencija ugovora')}
                tooltip={t(
                  'forms:templateContractKindHint',
                  'Kad se po ovom šablonu kreira dokument, upisuje se novi ugovor ili aneks u istoriju ugovora zaposlenog. Forma tada traži podatke o ugovoru.'
                )}
                style={{ minWidth: 200 }}
              >
                <Select
                  options={CONTRACT_KIND_OPTIONS.map((v) => ({
                    value: v,
                    label: t(
                      `forms:templateContractKind_${v || 'none'}`,
                      v || '—'
                    ),
                  }))}
                />
              </Form.Item>
            )}
            {audienceValue === 'employee' && (
              <Form.Item
                name="emailDelivery"
                label={t('forms:templateEmailDelivery', 'Slanje mejlom')}
                tooltip={t('forms:templateEmailDeliveryHint')}
                style={{ minWidth: 200 }}
              >
                <Select
                  options={EMAIL_DELIVERY_OPTIONS.map((v) => ({
                    value: v,
                    label: t(`forms:templateEmailDelivery_${v || 'none'}`, v || '—'),
                  }))}
                />
              </Form.Item>
            )}
            {audienceValue === 'employee' && !contractKindValue && (
              <Form.Item
                name="autoGenerateOnLeaveType"
                label={t(
                  'forms:autoGenerateOnLeaveType',
                  'Auto-generisanje na odobrenje'
                )}
                tooltip={t(
                  'forms:autoGenerateOnLeaveTypeHint',
                  'Kad se odobri zahtev ovog tipa, dokument se automatski kreira. Ostavi prazno za ručno/grupno.'
                )}
                style={{ minWidth: 240 }}
              >
                <Select
                  allowClear
                  placeholder={t(
                    'forms:noAutoGenerate',
                    'Bez auto-generisanja'
                  )}
                  options={LEAVE_TYPE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: t(`forms:leaveType_${o.value}`, o.label),
                  }))}
                />
              </Form.Item>
            )}
          </Space>

          {audienceValue === 'employee' &&
            !contractKindValue &&
            autoGenLeaveType && (
              <Form.Item
                name="replacesDefaultDocument"
                valuePropName="checked"
                style={{ marginTop: 4, marginBottom: 0 }}
                tooltip={
                  autoGenLeaveType === 'termination'
                    ? t('forms:replacesTerminationDocumentHint')
                    : t(
                        'forms:replacesDefaultDocumentHint',
                        'Kad se odobri zahtev ovog tipa, umesto podrazumevanog HLB dokumenta koristi se ovaj šablon. Dozvoljen je najviše jedan takav šablon po tipu odsustva.'
                      )
                }
              >
                <Checkbox>
                  {t(
                    'forms:replacesDefaultDocument',
                    'Zameni podrazumevani HLB dokument'
                  )}
                </Checkbox>
              </Form.Item>
            )}

          {canManageGlobal && (
            <>
              <Divider style={{ margin: '4px 0 12px' }} />
              <Space
                size="large"
                align="start"
                style={{ width: '100%', marginBottom: 16 }}
              >
                <Checkbox
                  checked={isGlobal}
                  onChange={(e) => {
                    setIsGlobal(e.target.checked);
                    setIsDirty(true);
                  }}
                >
                  {t(
                    'forms:globalTemplate',
                    'Globalni šablon (dostupan više firmi)'
                  )}
                </Checkbox>
                {isGlobal && (
                  <Form.Item
                    name="allowedCompanies"
                    label={t('forms:allowedCompanies', 'Dostupno firmama')}
                    tooltip={t(
                      'forms:allowedCompaniesHint',
                      'Ostavi prazno da bude dostupno SVIM firmama.'
                    )}
                    style={{ flex: 1, minWidth: 320, marginBottom: 0 }}
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder={t('forms:allCompanies', 'Sve firme')}
                      options={companyOptions}
                      filterOption={(input, option) =>
                        (option?.label || '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                )}
              </Space>
            </>
          )}
        </Form>

        {showUploadUi ? (
          <div>
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 px-3 py-2">
              <FileWordOutlined style={{ color: '#2b579a' }} />
              <span className="font-medium">
                {replacementFile?.name ||
                  file?.name ||
                  (isEditingUpload
                    ? t('forms:existingWordTemplate', 'Postojeći Word dokument')
                    : t('forms:noFileSelected', 'Izaberi Word (.docx) fajl.'))}
              </span>
              {replacementFile && (
                <Tag color="orange">
                  {t('forms:willReplace', 'Zameniće postojeći')}
                </Tag>
              )}
              {isEditingUpload && (
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    size="small"
                    type="primary"
                    className="!rounded-[var(--buttonRadius)]"
                    icon={<EditOutlined />}
                    // A picked replacement file is not saved yet; editing the
                    // stored one would be overwritten by it.
                    disabled={Boolean(replacementFile)}
                    onClick={() => setWordEditorOpen(true)}
                  >
                    {t('forms:editInApp', 'Uredi u aplikaciji')}
                  </Button>
                  <Button
                    size="small"
                    className="!rounded-[var(--buttonRadius)]"
                    icon={<DownloadOutlined />}
                    onClick={() =>
                      downloadDocumentTemplateFile(
                        template._id,
                        `${template?.name || 'template'}.docx`
                      )
                    }
                  >
                    {t('forms:downloadDocx', 'Preuzmi')}
                  </Button>
                  <Upload
                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    showUploadList={false}
                    beforeUpload={(f) => {
                      setReplacementFile(f);
                      setIsDirty(true);
                      return false;
                    }}
                  >
                    <Button
                      size="small"
                      className="!rounded-[var(--buttonRadius)]"
                      icon={<UploadOutlined />}
                    >
                      {t('forms:replaceDocx', 'Zameni dokument')}
                    </Button>
                  </Upload>
                </div>
              )}
            </div>
            <Alert
              className="mt-3"
              type="info"
              showIcon
              message={t('forms:uploadTemplateTokenHint', {
                // Company documents have no employee, so the example must be a
                // token their palette actually offers.
                example:
                  audienceValue === 'employee'
                    ? '{employeeFullName}'
                    : '{companyName}',
              })}
            />
            <div className="mt-3">
              <div className="mb-1 font-medium">
                {t('customTemplateTokens.paletteTitle', 'Oznake')}
              </div>
              {/* No editor to insert into (it's a Word file), so clicking a chip
                copies the raw {token} to paste into the document. */}
              <TokenChips
                tokens={visibleTokens}
                customFieldKeys={customFieldKeys}
              />
            </div>
          </div>
        ) : (
          <DocumentTemplateEditor
            value={contentHtml}
            onChange={(html) => {
              setContentHtml(html);
              setIsDirty(true);
            }}
            tokens={visibleTokens}
            customFieldKeys={customFieldKeys}
            placeholder={t(
              'forms:templateBodyPlaceholder',
              'Tekst dokumenta...'
            )}
          />
        )}

        <div style={{ marginTop: 16 }}>
          <Space
            style={{ width: '100%', justifyContent: 'space-between' }}
            align="center"
          >
            <strong>
              {t(
                'forms:adHocFields',
                'Dodatna polja (unose se pri generisanju)'
              )}
            </strong>
            <Button
              className="btn-default btn-default--hover"
              icon={<PlusOutlined />}
              onClick={() =>
                changeCustomFields((prev) => [...prev, emptyField()])
              }
            >
              {t('forms:addField', 'Dodaj polje')}
            </Button>
          </Space>
          <Table
            size="small"
            rowKey={(_, i) => i}
            columns={fieldColumns}
            dataSource={customFields}
            pagination={false}
            style={{ marginTop: 8 }}
            locale={{
              emptyText: t('forms:noAdHocFields', 'Nema dodatnih polja'),
            }}
          />
          {unplacedFieldKeys.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={t('forms:fieldsNotInText', {
                defaultValue:
                  'Nisu ubačena u tekst: {{fields}}. Postavite kursor gde vrednost treba da stoji i kliknite zeleni čip u paleti oznaka.',
                fields: unplacedFieldKeys.join(', '),
                interpolation: { escapeValue: false },
              })}
            />
          )}
        </div>
      </Modal>
      {/* A sibling, not a child of the modal above: a closing modal freezes its
        content, which left the editor stuck open after "Save and edit". */}
      {(isEditingUpload || uploadedTemplate) && (
        <TemplateWordEditorModal
          open={wordEditorOpen}
          template={uploadedTemplate || template}
          palette={wordPalette}
          onClose={() => {
            setWordEditorOpen(false);
            if (uploadedTemplate) {
              setUploadedTemplate(null);
              onClose?.();
            }
          }}
          onSaved={(saved) => {
            message.success(
              t('forms:templateFileSaved', 'Word šablon je sačuvan.')
            );
            warnMissingWordFields(saved);
            queryClient.invalidateQueries({
              queryKey: ['documentTemplates', companyId],
            });
          }}
        />
      )}
    </>
  );
}
